// node_modules
import { useCallback, useContext, useEffect, useState } from "react";
// Constants
import { HubConstants } from "Constants";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Enums
import { CaseActionTypeEnum, ToastTypeEnum } from "Enums";
// Contexts
import { WebsocketContext } from "Providers";
import { CaseContext, AuthContext } from "Stores";
// Types
import { TNotificationMessageDTO, TTechnology } from "Types";
// Interfaces
import { ISetCaseConceptTechnologyListAction } from "Interfaces";

export const useAdditionalTechnology = (): 
        [(conceptNumbering?: string | undefined, technologyName?: string | undefined) => Promise<void>, TTechnology | undefined] => {
    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { webSocketController } = useContext(WebsocketContext);
    
    // State
    const [additionalTechnologiesResourceName, setAdditionalTechnologiesResourceName] = useState<string>("");
    const [lastAdditionalTechnologyCreated, setLastAdditionalTechnologyCreated] = useState<TTechnology | undefined>(undefined);

    // Logic
    useEffect(() => {
        if (!caseState.selectedCaseId) {
            setAdditionalTechnologiesResourceName("");
            return;
        }

        setAdditionalTechnologiesResourceName(`${HubConstants.ADDITIONAL_TECHNOLOGIES_RESOURCE_NAME_PREFIX}${caseState.selectedCaseId}`);
    }, [caseState.selectedCaseId]);

    const addAdditionalTechnology = async (conceptNumbering?: string, technologyName: string | undefined = undefined): Promise<void> => {
        if(!caseState.selectedCaseId) { return; }
        
        const isSuccess: boolean = 
            await TechnologyControllerSingleton
                .createAdditionalTechnology(caseState.selectedCaseId, conceptNumbering, technologyName);

        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to create an additional technology.");
            return;
        }
    };

    const handleNotificationMessages = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        ToastHelperSingleton.handleNotificationMessage(notificationMessage, authState.userEmail);
    }, [authState]);

    const handleAdditionalTechnologyCreated = useCallback((newTechnology: TTechnology) => {
        if(!caseState.conceptTechnologyList) { return; }
        
        // add new additional technology to additional technologies list in concepts technologies list of case state
        const currentCaseConceptTechnologyList = caseState.conceptTechnologyList;
        currentCaseConceptTechnologyList.additionalTechnologies.push(newTechnology);
        caseDispatch({ type: CaseActionTypeEnum.SetCaseConceptTechnologyList, 
            caseConceptTechnologyList: currentCaseConceptTechnologyList} as ISetCaseConceptTechnologyListAction);
        setLastAdditionalTechnologyCreated(newTechnology);
    }, [caseDispatch, caseState.conceptTechnologyList]);

    // manage web sockets related events
    useEffect(() => {
        // safety-check
        if (!additionalTechnologiesResourceName) { return; }
        
        // we add two handlers to listen on create and notification messages related to additional technologies resource name
        webSocketController.addHandler(`${HubConstants.CREATE_PREFIX}${additionalTechnologiesResourceName}`, 
            handleAdditionalTechnologyCreated);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${additionalTechnologiesResourceName}`, 
            handleNotificationMessages);
        
        // remove handlers on clean up time
        return () => {
            webSocketController.removeHandler(`${HubConstants.CREATE_PREFIX}${additionalTechnologiesResourceName}`, 
                handleAdditionalTechnologyCreated);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${additionalTechnologiesResourceName}`, 
                handleNotificationMessages);
        };
    }, [additionalTechnologiesResourceName, handleAdditionalTechnologyCreated, handleNotificationMessages, webSocketController]);

    return [addAdditionalTechnology, lastAdditionalTechnologyCreated];
};
